/*
Stack-based Douglas Peucker line simplification routine
returned is a reduced GLatLng array.

After code by  Dr. Gary J. Robinson,
Environmental Systems Science Centre,
University of Reading, Reading, UK

https://gist.github.com/enjoylife/4042232
*/

const simplifyPath = (source: google.maps.LatLng[], tolerance: number) => {
  let stack: number, dest: number, start: number, end: number, i: number, sig: number;
  let devSqr: number, maxDevSqr: number, bandSqr: number;
  let x12: number,
    y12: number,
    d12: number,
    x13: number,
    y13: number,
    d13: number,
    x23: number,
    y23: number,
    d23: number;
  const F = (Math.PI / 180.0) * 0.5;

  const index = [];
  const sigStart = [];
  const sigEnd = [];

  if (source.length < 3) {
    return source;
  }

  const sourceLength = source.length;
  bandSqr = (tolerance * 360.0) / (2.0 * Math.PI * 6378137.0); /* Now in degrees */
  bandSqr *= bandSqr;
  dest = 0;
  sigStart[0] = 0;
  sigEnd[0] = sourceLength - 1;
  stack = 1;

  while (stack > 0) {
    start = sigStart[stack - 1];
    end = sigEnd[stack - 1];
    stack--;

    if (end - start > 1) {
      x12 = source[end].lng() - source[start].lng();
      y12 = source[end].lat() - source[start].lat();

      if (Math.abs(x12) > 180.0) {
        x12 = 360.0 - Math.abs(x12);
      }

      x12 *= Math.cos(
        F * (source[end].lat() + source[start].lat())
      ); /* use avg lat to reduce lng */
      d12 = x12 * x12 + y12 * y12;

      for (i = start + 1, sig = start, maxDevSqr = -1.0; i < end; i++) {
        x13 = source[i].lng() - source[start].lng();
        y13 = source[i].lat() - source[start].lat();
        if (Math.abs(x13) > 180.0) {
          x13 = 360.0 - Math.abs(x13);
        }
        x13 *= Math.cos(F * (source[i].lat() + source[start].lat()));
        d13 = x13 * x13 + y13 * y13;

        x23 = source[i].lng() - source[end].lng();
        y23 = source[i].lat() - source[end].lat();
        if (Math.abs(x23) > 180.0) {
          x23 = 360.0 - Math.abs(x23);
        }
        x23 *= Math.cos(F * (source[i].lat() + source[end].lat()));
        d23 = x23 * x23 + y23 * y23;

        if (d13 >= d12 + d23) {
          devSqr = d23;
        } else if (d23 >= d12 + d13) {
          devSqr = d13;
        } else {
          devSqr = ((x13 * y12 - y13 * x12) * (x13 * y12 - y13 * x12)) / d12;
        } // solve triangle

        if (devSqr > maxDevSqr) {
          sig = i;
          maxDevSqr = devSqr;
        }
      }

      if (maxDevSqr < bandSqr) {
        index[dest] = start;
        dest++;
      } else {
        stack++;
        sigStart[stack - 1] = sig;
        sigEnd[stack - 1] = end;
        stack++;
        sigStart[stack - 1] = start;
        sigEnd[stack - 1] = sig;
      }
    } else {
      index[dest] = start;
      dest++;
    }
  }

  index[dest] = sourceLength - 1;
  dest++;

  const result = [];
  for (let i = 0; i < dest; i++) {
    result.push(source[index[i]]);
  }

  return result;
};

export default simplifyPath;
